import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Snowflake, 
  Calendar, 
  Clock, 
  MapPin, 
  Ticket, 
  Users, 
  Music, 
  UtensilsCrossed,
  PartyPopper,
  ChevronDown,
  Phone,
  Mail,
  HelpCircle,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Menu,
  X,
  Instagram,
  Facebook
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import axios from "axios";
import { toast } from "sonner";
import { SilvesterGallery } from "@/components/SilvesterGallery";
import { WeihnachtsfeierTeaser } from "@/components/WeihnachtsfeierTeaser";
import { PartnerLogos } from "@/components/PartnerLogos";
import { SilvesterCountdown } from "@/components/SilvesterCountdown";
import { useVipAvailability, VipAvailabilityBar } from "@/components/VipAvailability";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact Form Component
const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.message) {
      toast.error("Bitte fülle alle Pflichtfelder aus");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Anfrage erfolgreich gesendet!");
    } catch (error) {
      toast.error("Fehler beim Senden der Anfrage");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="text-5xl mb-4">✅</div>
        <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
          Vielen Dank für deine Anfrage!
        </h3>
        <p className="text-slate-600">
          Wir melden uns innerhalb von 24 Stunden bei dir.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label htmlFor="contact-name" className="text-slate-700">Name *</Label>
          <Input
            id="contact-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Dein Name"
            className="mt-2 h-12 rounded-xl border-slate-200"
            required
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <Label htmlFor="contact-email" className="text-slate-700">E-Mail *</Label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="ihre@email.de"
            className="mt-2 h-12 rounded-xl border-slate-200"
            required
            data-testid="contact-email-input"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <Label htmlFor="contact-phone" className="text-slate-700">Telefon *</Label>
        <Input
          id="contact-phone"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          placeholder="+49 123 456789"
          className="mt-2 h-12 rounded-xl border-slate-200"
          required
          data-testid="contact-phone-input"
        />
      </div>
      
      <div className="mb-6">
        <Label htmlFor="contact-message" className="text-slate-700">Nachricht *</Label>
        <Textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Deine Nachricht an uns (Fragen, Veranstaltungsanfragen, Anregungen...)"
          className="mt-2 min-h-[120px] rounded-xl border-slate-200 resize-none"
          required
          data-testid="contact-message-input"
        />
      </div>
      
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-lg shadow-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100"
        data-testid="contact-submit-btn"
      >
        <Mail className="w-5 h-5 mr-2" />
        {loading ? "Wird gesendet..." : "Anfrage senden"}
      </Button>
      
      <p className="text-center text-sm text-slate-500 mt-4">
        Wir melden uns innerhalb von 24 Stunden bei dir.<br />
        Es gelten die <Link to="/datenschutz" className="text-sky-600 hover:underline">Datenschutzerklärungen</Link>.
      </p>
    </form>
  );
};

const LandingPage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const vipAvailability = useVipAvailability();
  const vipSoldOut = vipAvailability?.free <= 0;

  // Countdown to opening date: November 23, 2026 at 10:00 German time
  useEffect(() => {
    // November 23, 2026, 10:00 AM German time (CET = UTC+1)
    const openingDate = new Date('2026-11-27T09:00:00Z');
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = openingDate - now;
      
      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top fixed wrapper: countdown + nav stacked so they never overlap */}
      <div className="fixed top-0 left-0 right-0 z-50">
        {/* Countdown Banner */}
        <div className="relative bg-gradient-to-r from-sky-600 via-sky-500 to-sky-600 text-white py-2 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 ice-pattern opacity-20" />
        <div className="relative flex items-center justify-center gap-2 flex-wrap">
          <span className="text-lg">⏳</span>
          <span className="font-medium">Countdown bis zur Eröffnung:</span>
          <div className="flex items-center gap-3 ml-2">
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold tabular-nums" data-testid="countdown-days">{countdown.days}</span>
              <span className="text-xs uppercase tracking-wider opacity-80">Tage</span>
            </div>
            <span className="text-xl font-light">:</span>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold tabular-nums" data-testid="countdown-hours">{String(countdown.hours).padStart(2, '0')}</span>
              <span className="text-xs uppercase tracking-wider opacity-80">Std</span>
            </div>
            <span className="text-xl font-light">:</span>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold tabular-nums" data-testid="countdown-minutes">{String(countdown.minutes).padStart(2, '0')}</span>
              <span className="text-xs uppercase tracking-wider opacity-80">Min</span>
            </div>
            <span className="text-xl font-light">:</span>
            <div className="flex flex-col items-center">
              <span className="text-2xl font-bold tabular-nums" data-testid="countdown-seconds">{String(countdown.seconds).padStart(2, '0')}</span>
              <span className="text-xs uppercase tracking-wider opacity-80">Sek</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Snowflakes */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
        {[...Array(15)].map((_, i) => (
          <Snowflake
            key={i}
            className="snowflake animate-snowfall text-sky-200/40"
            style={{
              left: `${Math.random() * 100}%`,
              animationDuration: `${8 + Math.random() * 10}s`,
              animationDelay: `${Math.random() * 5}s`,
              fontSize: `${0.5 + Math.random() * 1}rem`,
            }}
          />
        ))}
      </div>

      {/* Navigation */}
      <nav className="relative glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1 lg:flex-none lg:shrink-0">
            <Snowflake className="w-7 h-7 sm:w-8 sm:h-8 text-sky-500 shrink-0" />
            <span className="text-base sm:text-xl font-semibold text-slate-900 truncate" style={{ fontFamily: 'Playfair Display' }}>
              Grömitzer Eiszeit
            </span>
          </div>
          <div className="hidden lg:flex items-center gap-3 xl:gap-4 2xl:gap-6 text-sm 2xl:text-base whitespace-nowrap">
            <button 
              onClick={() => scrollToSection("info")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-info"
            >
              Informationen
            </button>
            <button 
              onClick={() => scrollToSection("hours")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-hours"
            >
              Öffnungszeiten
            </button>
            <button 
              onClick={() => scrollToSection("prices")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-prices"
            >
              Preise
            </button>
            <button 
              onClick={() => scrollToSection("eisstock")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-eisstock"
            >
              Eisstockschießen
            </button>
            <button 
              onClick={() => scrollToSection("weihnachtsfeier")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-weihnachtsfeier"
            >
              Weihnachtsfeier
            </button>
            <button 
              onClick={() => scrollToSection("silvester")} 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-silvester"
            >
              Silvester
            </button>
            <Link 
              to="/musik-und-meer" 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-musik"
            >
              Musik & Meer
            </Link>
            <Link 
              to="/gutschein" 
              className="text-slate-600 hover:text-sky-500 transition-colors"
              data-testid="nav-gutschein"
            >
              Gutschein
            </Link>
          </div>
          <Link to="/silvester" className="hidden xl:block shrink-0">
            <Button 
              className="h-10 px-6 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-medium shadow-lg shadow-sky-500/20"
              data-testid="nav-silvester-btn"
            >
              <PartyPopper className="w-4 h-4 mr-2" />
              Silvester
            </Button>
          </Link>

          {/* Mobile burger button */}
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="lg:hidden p-1.5 text-slate-900 hover:text-sky-600 transition-colors shrink-0"
            aria-label={mobileMenuOpen ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={mobileMenuOpen}
            data-testid="nav-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="w-9 h-9" strokeWidth={2.5} /> : <Menu className="w-9 h-9" strokeWidth={2.5} />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-sky-100 bg-white/95 backdrop-blur-md" data-testid="nav-mobile-panel">
            <div className="px-6 py-4 flex flex-col">
              <button
                onClick={() => scrollToSection("info")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-info"
              >
                Informationen
              </button>
              <button
                onClick={() => scrollToSection("hours")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-hours"
              >
                Öffnungszeiten
              </button>
              <button
                onClick={() => scrollToSection("prices")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-prices"
              >
                Preise
              </button>
              <button
                onClick={() => scrollToSection("eisstock")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-eisstock"
              >
                Eisstockschießen
              </button>
              <button
                onClick={() => scrollToSection("weihnachtsfeier")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-weihnachtsfeier"
              >
                Weihnachtsfeier
              </button>
              <button
                onClick={() => scrollToSection("silvester")}
                className="py-3 text-left text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-silvester"
              >
                Silvester
              </button>
              <Link
                to="/musik-und-meer"
                onClick={() => setMobileMenuOpen(false)}
                className="py-3 text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-musik"
              >
                Musik & Meer
              </Link>
              <Link
                to="/gutschein"
                onClick={() => setMobileMenuOpen(false)}
                className="py-3 text-slate-700 hover:text-sky-600 border-b border-slate-100"
                data-testid="nav-mobile-gutschein"
              >
                Gutschein
              </Link>
              <Link to="/silvester" onClick={() => setMobileMenuOpen(false)}>
                <Button
                  className="w-full mt-4 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-medium"
                  data-testid="nav-mobile-silvester-btn"
                >
                  <PartyPopper className="w-4 h-4 mr-2" />
                  Silvestertickets
                </Button>
              </Link>
            </div>
          </div>
        )}
      </nav>
      </div>

      {/* Hero Section */}
      <section className="hero-section relative">
        <div 
          className="absolute inset-0 bg-cover bg-center lg:bg-[position:30%_35%]"
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(12, 74, 110, 0.6) 0%, rgba(3, 105, 161, 0.55) 50%, rgba(14, 165, 233, 0.5) 100%), url('https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/7nyh004p_KENNEDY%20PRODUCTION-2321.jpg')`
          }}
        />
        
        {/* Hero Content */}
        <div className="relative z-10 min-h-screen flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-white animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">27.11.2026 - 06.01.2027</span>
              </div>
              
              <h1 
                className="text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-6"
                style={{ fontFamily: 'Playfair Display' }}
                data-testid="hero-title"
              >
                Schlittschuhlaufen
                <br />
                <span className="text-sky-200">mit Meerblick</span>
              </h1>
              
              <p className="text-lg md:text-xl text-sky-100 leading-relaxed mb-8 max-w-xl">
                Das geht bei der Grömitzer Eiszeit! Auf der Wiese an der Uferstraße erwartet euch 
                ein richtiges Winterparadies mit Eisbahn, Eisstockschießen und Livemusik.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/silvester">
                  <Button 
                    className="h-14 px-8 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold text-lg shadow-lg shadow-amber-500/30 transition-all hover:scale-105"
                    data-testid="hero-silvester-btn"
                  >
                    <Ticket className="w-5 h-5 mr-2" />
                    Silvestertickets
                  </Button>
                </Link>
                <Link to="/eisstock">
                  <Button 
                    variant="outline"
                    className="h-14 px-8 rounded-full bg-white/10 hover:bg-white/20 text-white border-white/30 font-semibold text-lg backdrop-blur-sm"
                    data-testid="hero-eisstock-btn"
                  >
                    <Users className="w-5 h-5 mr-2" />
                    Eisstockschießen
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right: Feature Image */}
            <div className="relative hidden lg:block animate-fade-in-up animation-delay-300">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-transform duration-500">
                <video
                  src="https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/foomjbai_Zweites%20Reel%20Gro%CC%88mitz.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/50 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-4 shadow-xl animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-sky-500 flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Standort</p>
                    <p className="font-semibold text-slate-900">Uferstraße, Grömitz</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <button 
          onClick={() => scrollToSection("info")}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 hover:text-white transition-colors animate-bounce"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="w-8 h-8" />
        </button>
      </section>

      {/* Info Section */}
      <section id="info" className="py-24 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-sm font-medium tracking-widest uppercase text-sky-500 mb-4 block">
              Attraktionen
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="info-title"
            >
              Ein echtes Winterparadies
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Genießt die einzigartige Atmosphäre direkt an der Ostsee mit vielen Highlights für die ganze Familie.
            </p>
          </div>
          
          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[300px]">
            {/* Large Card - Ice Skating */}
            <Card className="feature-card md:col-span-2 md:row-span-2 overflow-hidden border-0 shadow-lg group">
              <div className="relative h-full img-hover-zoom">
                <img 
                  src="https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/68lbge6d_P1425477.jpg" 
                  alt="Eisbahn" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <div className="inline-flex items-center gap-2 bg-sky-500/80 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
                    <Snowflake className="w-4 h-4" />
                    <span className="text-sm font-medium">Hauptattraktion</span>
                  </div>
                  <h3 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Eislaufen mit Meerblick
                  </h3>
                  <p className="text-sky-100 text-lg">
                    Dreht gemütlich eure Runden auf unserer Eisbahn und genießt dabei den einzigartigen Blick auf die Ostsee.
                  </p>
                </CardContent>
              </div>
            </Card>
            
            {/* Eisstockschießen */}
            <Card className="feature-card overflow-hidden border-0 shadow-lg group" data-testid="eisstock-card">
              <div className="relative h-full img-hover-zoom">
                <img 
                  src="https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/6mwvyify_eisstockschiessen.jpg" 
                  alt="Eisstockschießen" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <Users className="w-8 h-8 mb-3 text-amber-400" />
                  <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>
                    Eisstockschießen
                  </h3>
                  <p className="text-sky-100 text-sm">
                    Probiert euch im traditionellen Eisstockschießen aus!
                  </p>
                </CardContent>
              </div>
            </Card>
            
            {/* Livemusik */}
            <Link to="/musik-und-meer" data-testid="music-card-link" aria-label="Zur Musik & Meer Seite">
              <Card className="feature-card overflow-hidden border-0 shadow-lg group cursor-pointer hover:shadow-xl transition-shadow" data-testid="music-card">
                <div className="relative h-full img-hover-zoom">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eiswelt-silvester/artifacts/1ljmn8je_Eiszeit.jpg" 
                    alt="Musik & Meer" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                  <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <Music className="w-8 h-8 mb-3 text-pink-400" />
                    <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>
                      Musik & Meer
                    </h3>
                    <p className="text-sky-100 text-sm">
                      Heiße Beats im Almhüttenzelt – Sei dabei!
                    </p>
                    <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold text-pink-300 group-hover:text-pink-200">
                      Programm ansehen →
                    </span>
                  </CardContent>
                </div>
              </Card>
            </Link>
            
            {/* Leckereien */}
            <Card className="feature-card md:col-span-2 overflow-hidden border-0 shadow-lg group" data-testid="food-card">
              <div className="relative h-full img-hover-zoom">
                <img 
                  src="https://customer-assets.emergentagent.com/job_eiswelt-silvester/artifacts/9c4x0sbs_IMG_2546.jpg" 
                  alt="Almhüttenzelt" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=800&q=80";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 p-6 text-white max-w-md">
                  <h3 className="text-2xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    Almhüttenzelt – Wärmstube & Treffpunkt
                  </h3>
                  <UtensilsCrossed className="w-8 h-8 mb-3 text-amber-400" />
                  <h4 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>
                    Herzhafte & süße Leckereien
                  </h4>
                  <p className="text-sky-100 text-sm">
                    Wärmt euch auf mit Glühwein, heißer Schokolade und winterlichen Köstlichkeiten.
                  </p>
                </CardContent>
              </div>
            </Card>

            {/* Außenbereich */}
            <Card className="feature-card overflow-hidden border-0 shadow-lg group" data-testid="outdoor-card">
              <div className="relative h-full img-hover-zoom">
                <img
                  src="https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/kf7lfsgq_KENNEDY%20PRODUCTION-2359.jpg"
                  alt="Gemütlicher Außenbereich"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-amber-950/85 via-amber-900/30 to-transparent" />
                <CardContent className="absolute bottom-0 left-0 right-0 p-6 text-white">
                  <div className="inline-flex items-center gap-2 bg-amber-500/80 backdrop-blur-sm rounded-full px-3 py-1 mb-3">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm font-medium">Gemütlich</span>
                  </div>
                  <h3 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display' }}>
                    Unser Außenbereich
                  </h3>
                  <p className="text-amber-50 text-sm">
                    Winterliche Gemütlichkeit unter freiem Himmel – mit heißen Getränken, duftenden Crêpes, herzhaften Leckereien und liebevoll gestalteten Plätzen zum Wohlfühlen und Verweilen.
                  </p>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Opening Hours Section */}
      <section id="hours" className="py-24 md:py-32 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-sky-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-sky-500 mb-4 block">
              Wann sind wir da?
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="hours-title"
            >
              Öffnungszeiten
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Regular Hours */}
            <Card className="hours-card border-0 shadow-lg p-8" data-testid="hours-regular">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                  Regulär
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">Mo - Fr</span>
                  <span className="font-semibold text-slate-900">13 - 21 Uhr</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">Sa - So</span>
                  <span className="font-semibold text-slate-900">10 - 21 Uhr</span>
                </div>
                <p className="text-xs text-slate-500 pt-1 leading-relaxed">
                  Am Wochenende bei Veranstaltungen längere Öffnungszeiten.
                </p>
              </div>
            </Card>
            
            {/* Holiday Hours */}
            <Card className="hours-card border-0 shadow-lg p-8" data-testid="hours-holiday">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-amber-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                  Feiertage
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">24.12.</span>
                  <span className="font-semibold text-slate-900">10 - 15 Uhr</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600">25.12.</span>
                  <span className="font-semibold text-slate-900">12 - 18 Uhr</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">26.12.</span>
                  <span className="font-semibold text-slate-900">10 - 18 Uhr</span>
                </div>
              </div>
            </Card>
            
            {/* New Year Hours */}
            <Card className="hours-card border-0 shadow-lg p-8 bg-gradient-to-br from-amber-50 to-amber-100/50" data-testid="hours-newyear">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                  <PartyPopper className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                  Silvester & Neujahr
                </h3>
              </div>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600">27.12. - 04.01.</span>
                  <span className="font-semibold text-slate-900">10 - 22 Uhr</span>
                </div>
                <p className="text-sm text-amber-700 mt-4">
                  Täglich erweiterte Öffnungszeiten für die Feiertage!
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Prices Section */}
      <section id="prices" className="py-24 md:py-32 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-sky-500 mb-4 block">
              Preise
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="prices-title"
            >
              Unsere Eisbahn Preise
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Eintritt Eisbahn */}
            <Card className="border-0 shadow-lg p-8 hover:shadow-xl transition-shadow" data-testid="price-rental">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <Snowflake className="w-6 h-6 text-sky-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                  Eintritt Eisbahn
                </h3>
              </div>
              <div className="space-y-4">
                <div className="bg-sky-50 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-slate-700 font-medium">Bis 12 Jahre</span>
                    <span className="text-2xl font-bold text-sky-600">7,00 €</span>
                  </div>
                  <span className="text-sm text-slate-500">(2 Stunden)</span>
                </div>
                <div className="bg-sky-50 rounded-xl p-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-slate-700 font-medium">Ab 13 Jahre</span>
                    <span className="text-2xl font-bold text-sky-600">9,00 €</span>
                  </div>
                  <span className="text-sm text-slate-500">(2 Stunden)</span>
                </div>
              </div>
            </Card>
            
            {/* Verleih */}
            <Card className="border-0 shadow-lg p-8 hover:shadow-xl transition-shadow bg-gradient-to-br from-sky-50 to-white" data-testid="price-penguin">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
                  <span className="text-2xl">🐧</span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                  Verleih
                </h3>
              </div>
              <div className="space-y-4">
                <div className="bg-sky-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700 font-medium">Pinguin Hilfe</span>
                    <span className="text-2xl font-bold text-sky-600">5,00 €</span>
                  </div>
                </div>
                <div className="bg-sky-50 rounded-xl p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-700 font-medium">Schlittschuhe</span>
                    <span className="text-2xl font-bold text-sky-600">5,00 €</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Hinweis: Eisbahn-Verkleinerung bei Eisstockschießen */}
          <div className="max-w-3xl mx-auto mt-10" data-testid="eisbahn-notice">
            <div className="flex items-start gap-4 bg-amber-50 border border-amber-200 rounded-2xl p-5 md:p-6 shadow-sm">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Playfair Display' }}>
                  Hinweis zur Eisfläche
                </h4>
                <p className="text-slate-700 text-sm md:text-base leading-relaxed">
                  Bitte beachte: Während reservierter Zeiten für <strong>Eisstockschießen</strong> kann die 
                  nutzbare Fläche der Eisbahn vorübergehend verkleinert werden, da einzelne Bahnen für die 
                  Reservierungen abgetrennt werden. Der Eislauf-Bereich bleibt selbstverständlich weiterhin geöffnet – 
                  allerdings mit etwas eingeschränktem Platzangebot. Wir bitten um euer Verständnis.
                </p>
              </div>
            </div>
          </div>

          {/* Gutschein CTA */}
          <div className="max-w-3xl mx-auto mt-6" data-testid="gutschein-cta">
            <Link to="/gutschein" className="block">
              <div className="flex items-start gap-4 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎁</span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Playfair Display' }}>
                    Gutschein verschenken
                  </h4>
                  <p className="text-slate-700 text-sm md:text-base leading-relaxed">
                    Verschenke winterlichen Zauber! Gutschein bequem online kaufen,
                    ausdrucken und verschenken.
                  </p>
                </div>
                <div className="hidden sm:flex items-center text-rose-600 font-medium">
                  Zum Gutschein →
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Eisstockschießen Section */}
      <section id="eisstock" className="py-24 md:py-32 px-6 lg:px-12 bg-gradient-to-b from-slate-50 to-sky-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-sky-500 mb-4 block">
              Jetzt reservieren
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="eisstock-title"
            >
              Eisstockschießen
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Perfekt für Familie, Freunde oder Firmenevents!
            </p>
          </div>
          
          <div className="max-w-2xl mx-auto">
            <Card className="booking-card border-0 overflow-hidden shadow-xl" data-testid="eisstock-booking-card">
              <div className="relative h-56 img-hover-zoom">
                <img 
                  src="https://customer-assets.emergentagent.com/job_85938a33-e9d2-4396-b30a-3a2ddc28d1a7/artifacts/6mwvyify_eisstockschiessen.jpg" 
                  alt="Eisstockschießen" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
                <div className="absolute bottom-4 left-6">
                  <span className="inline-flex items-center gap-2 bg-sky-500 text-white rounded-full px-3 py-1 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    1,5 Stunden
                  </span>
                </div>
              </div>
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                    1 Bahn | bis zu 8 Personen | 1,5 Stunden
                  </h3>
                  <p className="text-4xl font-bold text-sky-600">100,00 €</p>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-4 mb-6">
                  <p className="text-slate-600 text-center mb-2">
                    Perfekt für Familie, Freunde oder Firmenevents!
                  </p>
                  <p className="text-slate-500 text-sm text-center">
                    Jede weitere Person: <span className="font-semibold">10,00 €</span>
                  </p>
                </div>
                
                <p className="text-amber-600 text-sm text-center mb-6 font-medium">
                  ⚠️ Buchung erforderlich! Zeitfenster werden nach Verfügbarkeit vergeben.
                </p>
                
                <Link to="/eisstock">
                  <Button 
                    className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold text-lg shadow-lg shadow-sky-500/20"
                    data-testid="book-eisstock-btn"
                  >
                    Bahn reservieren
                  </Button>
                </Link>
                <Link
                  to="/eisstockregeln"
                  className="mt-3 flex items-center justify-center gap-2 w-full h-11 rounded-full border-2 border-sky-500 text-sky-700 hover:bg-sky-50 hover:text-sky-800 font-semibold transition-colors"
                  data-testid="link-eisstockregeln-from-booking"
                >
                  📖 Spielregeln ansehen
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <WeihnachtsfeierTeaser />

      {/* Silvester Special Section */}
      <section id="silvester" className="py-24 md:py-32 px-6 lg:px-12 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-7xl mx-auto">
          <div
            className="max-w-4xl mx-auto mb-14 rounded-2xl border border-amber-400/50 bg-amber-400/10 backdrop-blur-md px-6 py-6 md:px-8 shadow-lg shadow-amber-500/10"
            data-testid="silvester-vip-notice"
          >
            <p className="text-lg md:text-xl font-semibold text-amber-300 mb-3">
              🎉 Gute Nachrichten: Silvester-VIP-Tickets wieder verfügbar!
            </p>
            <p className="text-slate-200 mb-2">
              Aufgrund der großen Nachfrage waren unsere VIP-Tickets für die Silvesterparty 2026 bereits ausverkauft. Jetzt gibt es eine zweite Chance!
            </p>
            <p className="text-slate-300">
              Nach einer erneuten sorgfältigen Überprüfung der Kapazitäten in unserem Almhüttenzelt können wir ein zusätzliches, begrenztes Kontingent an VIP-Tickets für unsere Silvesterparty am 31. Dezember 2026 freigeben.
            </p>
          </div>
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-amber-400 mb-4 block">
              🎆 31. Dezember 2026
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-white mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="silvester-title"
            >
              Silvester Special – VIP & Normal
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Feiert den Jahreswechsel im Almhüttenzelt mit Blick auf das Feuerwerk über der Ostsee!
            </p>
          </div>

          <SilvesterCountdown />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* VIP Ticket */}
            <Card className="border-0 overflow-hidden bg-gradient-to-br from-amber-50 to-amber-100/80 shadow-xl" data-testid="silvester-vip-card">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <span className="text-4xl mb-2 block">👑</span>
                  <h3 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                    Silvester VIP Ticket
                  </h3>
                </div>
                <div className="text-center mb-6">
                  <p className="text-4xl font-bold text-amber-600">49,00 €</p>
                  <span className="text-slate-500">pro Person</span>
                </div>
                <div className="space-y-3 text-slate-700 mb-6">
                  <p className="flex items-center gap-2">✨ Ab 18:00 Uhr</p>
                  <p className="flex items-center gap-2">🍽️ Buffet & Eintritt inklusive</p>
                  <p className="flex items-center gap-2">🪑 Sitzplatz reserviert</p>
                  <p className="flex items-center gap-2">🥂 Getränke separat</p>
                </div>
                <VipAvailabilityBar data={vipAvailability} />
                {vipSoldOut ? (
                  <Button
                    disabled
                    className="w-full h-12 rounded-full bg-slate-400 text-white font-semibold text-lg"
                    data-testid="book-silvester-vip-btn"
                  >
                    VIP Tickets ausverkauft
                  </Button>
                ) : (
                  <Link to="/silvester?type=vip">
                    <Button 
                      className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold text-lg shadow-lg"
                      data-testid="book-silvester-vip-btn"
                    >
                      VIP Tickets buchen
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
            
            {/* Normal Ticket */}
            <Card className="border-0 overflow-hidden bg-white shadow-xl" data-testid="silvester-normal-card">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <span className="text-4xl mb-2 block">🎉</span>
                  <h3 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
                    Silvester Normal Ticket
                  </h3>
                </div>
                <div className="text-center mb-6">
                  <p className="text-4xl font-bold text-sky-600">15,00 €</p>
                  <span className="text-slate-500">pro Person</span>
                </div>
                <div className="space-y-3 text-slate-700 mb-6">
                  <p className="flex items-center gap-2">✨ Ab 21:00 Uhr</p>
                  <p className="flex items-center gap-2">🎟️ Eintritt</p>
                  <p className="flex items-center gap-2">🍹 Getränke & Verpflegung separat</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                  <p className="text-amber-700 text-sm font-medium text-center">
                    📅 Vorverkauf startet am 15.12.2026
                  </p>
                </div>
                <Link to="/silvester?type=normal">
                  <Button 
                    className="w-full h-12 rounded-full bg-sky-500 hover:bg-sky-600 text-white font-semibold text-lg shadow-lg shadow-sky-500/20"
                    data-testid="book-silvester-normal-btn"
                  >
                    Normal Tickets buchen
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
          
          <div className="text-center mt-8">
            <div className="inline-block bg-amber-500/20 border border-amber-400/50 rounded-xl px-6 py-3">
              <p className="text-amber-300 font-medium">
                📅 Ticketvorverkauf für Normal Tickets startet am 15.12.2026
              </p>
            </div>
          </div>

          <SilvesterGallery />
        </div>
      </section>

      {/* Kontakt & Anfragen Section */}
      <section id="events" className="py-16 px-6 lg:px-12 bg-gradient-to-r from-sky-600 via-sky-500 to-sky-600">
        <div className="max-w-5xl mx-auto">
          <div className="glass rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="text-4xl mb-4">📩</div>
              <h2 
                className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-4"
                style={{ fontFamily: 'Playfair Display' }}
              >
                Kontakt & Anfragen
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Habt ihr Fragen oder plant ihr eine <strong>Weihnachtsfeier</strong>, ein <strong>Teambuilding</strong> oder ein <strong>Firmenevent</strong> bei uns? Wir freuen uns auf eure Nachricht!
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <span className="bg-white/80 text-slate-700 px-4 py-2 rounded-full text-sm font-medium">❓ Allgemeine Fragen</span>
                <a href="https://weihnachtsfeier.groemitzereiszeit.de/" target="_blank" rel="noopener noreferrer" className="bg-white/80 hover:bg-white text-slate-700 hover:text-emerald-800 px-4 py-2 rounded-full text-sm font-medium underline-offset-4 hover:underline transition-colors" data-testid="contact-weihnachtsfeier-link">🎄 Weihnachtsfeiern</a>
                <span className="bg-white/80 text-slate-700 px-4 py-2 rounded-full text-sm font-medium">🤝 Teambuilding</span>
                <span className="bg-white/80 text-slate-700 px-4 py-2 rounded-full text-sm font-medium">🏢 Firmenevents</span>
              </div>
            </div>
            
            {/* Contact Form */}
            <ContactForm />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 md:py-32 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm font-medium tracking-widest uppercase text-sky-500 mb-4 block">
              Häufige Fragen
            </span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="faq-title"
            >
              FAQ – Allgemeine Fragen
            </h2>
            <p className="text-lg text-slate-600">
              Antworten zu Buchung, Zahlung & Ablauf
            </p>
          </div>
          
          <Card className="border-0 shadow-xl p-6 md:p-8">
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-1">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Ich habe eigene Schlittschuhe, kann ich dann kostenlos laufen?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  <p><strong>Leider nein.</strong></p>
                  <p className="mt-3">
                    Das Mitbringen eigener Schlittschuhe ist selbstverständlich erlaubt, der Eintritt zur Eisbahn muss jedoch trotzdem bezahlt werden.
                  </p>
                  <p className="mt-3">
                    Als Betreiber der Grömitzer Eiszeit sind wir auf die Einnahmen angewiesen, um den Betrieb der Eisbahn sowie alle anfallenden Kosten decken zu können.
                  </p>
                  <p className="mt-3">
                    Wir bitten hierfür um dein Verständnis und freuen uns auf euren Besuch auf dem Eis.
                  </p>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-2">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Kann ich mit normalen Schuhen aufs Eis?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  Das ist nur zum Eisstockschießen erlaubt und dem Eisbahn-Personal zur Eisbearbeitung vorbehalten. In Ausnahmefällen wie z.B. bei Stürzen ist es natürlich den Helfern gestattet.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-3">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Darf ich mit den Schlittschuhen außerhalb der Eisbahn laufen?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  <strong>Nein.</strong> Die Kufen werden dadurch stumpf oder beschädigt (Kerben/Kratzer). Außerdem besteht eine hohe Rutsch- und Verletzungsgefahr auf festem Untergrund.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-4">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Darf ich Essen & Getränke aufs Eis mitnehmen?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  <strong>Nein</strong> – auf der Eisfläche sind Essen, Glasflaschen und heiße Getränke aus Sicherheitsgründen verboten.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-5" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-5">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Darf ich eigene Speisen und Getränke in der Almhütte verzehren?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  <strong>Nein.</strong> Das Verzehren von mitgebrachten Speisen und Getränken in der Almhütte ist nicht gestattet.
                  <br /><br />
                  Unsere Standbetreiber finanzieren den Betrieb ausschließlich über den Verkauf von Speisen und Getränken und müssen damit alle Kosten decken.
                  <br /><br />
                  Wir bitten daher um euer Verständnis und freuen uns, wenn ihr das gastronomische Angebot vor Ort nutzt.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-6" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-6">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Welche Zahlungsmittel werden akzeptiert?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  In der Regel <strong>Bar- und Kartenzahlung</strong>.
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-7" className="border-slate-200">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-7">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>Wie funktioniert Eisstockschießen – gibt es Regeln?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  Ja! Wir haben alle Regeln einfach und übersichtlich für dich zusammengestellt – von der Aufstellung über den Ablauf bis zur Wertung.{" "}
                  <Link to="/eisstockregeln" className="text-sky-600 hover:underline font-medium" data-testid="faq-link-eisstockregeln">
                    Hier geht's zu den Eisstockregeln →
                  </Link>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-8" className="border-slate-200 border-b-0">
                <AccordionTrigger className="text-left text-slate-900 font-medium hover:no-underline hover:text-sky-600" data-testid="faq-8">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="w-5 h-5 text-sky-500 mt-0.5 flex-shrink-0" />
                    <span>💳 Wie funktioniert die Online-Ticketbuchung?</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 pl-8">
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-slate-900 mb-1">Zahlungsarten für Online-Buchungen:</p>
                      <p><strong>PayPal</strong> – (PayPal, Kreditkarte, Lastschrift oder Überweisung über PayPal)</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                      <p className="text-green-800 text-sm">
                        📧 Nach Zahlung erhältst du eine Bestätigung per E-Mail.<br />
                        <strong>Diese dient als Ticket und ist beim Einlass vorzuzeigen.</strong>
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
        </div>
      </section>

      {/* Eisbahnordnung Section */}
      <section id="rules" className="py-24 md:py-32 px-6 lg:px-12 bg-gradient-to-b from-sky-50 to-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-4xl mb-4 block">⛸️</span>
            <h2 
              className="text-4xl md:text-5xl font-medium tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="rules-title"
            >
              Eisbahnordnung
            </h2>
            <p className="text-lg text-slate-600">
              Bitte beachtet folgende Regeln auf der Eisfläche:
            </p>
          </div>
          
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="bg-sky-600 p-4">
              <div className="flex items-center justify-center gap-2 text-white">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Wichtige Hinweise für eure Sicherheit</span>
              </div>
            </div>
            <CardContent className="p-8">
              <ul className="space-y-4">
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">⛸️</span>
                  <span className="text-slate-700">Eisfläche nur mit Schlittschuhen betreten</span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">🔄</span>
                  <span className="text-slate-700">Laufrichtung einhalten und rücksichtsvoll fahren</span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">🧤</span>
                  <span className="text-slate-700"><strong>Kinder bis 12 Jahre: Handschuh-Pflicht</strong></span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">🚫</span>
                  <span className="text-slate-700">Kein Essen, Trinken oder Rauchen auf dem Eis</span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">👮</span>
                  <span className="text-slate-700">Anweisungen des Ordnungspersonals sind zu befolgen</span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                  <span className="text-2xl">🎒</span>
                  <span className="text-slate-700">Keine Haftung für Garderobe und Wertgegenstände</span>
                </li>
                <li className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <span className="text-2xl">⚠️</span>
                  <span className="text-amber-800 font-medium">Betreten der Eisfläche auf eigene Gefahr</span>
                </li>
              </ul>
              
              <div className="mt-8 text-center">
                <p className="text-slate-600">
                  Vielen Dank für eure Rücksichtnahme und viel Spaß auf der Grömitzer Eiszeit! ❄️
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Partners & Sponsors */}
      <section id="partners" className="py-24 md:py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16 md:mb-20">
            <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 rounded-full px-4 py-1.5 mb-5 text-sm">
              <Sparkles className="w-4 h-4" />
              <span>Gemeinsam stark</span>
            </div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 tracking-tight text-slate-900" style={{ fontFamily: 'Playfair Display' }}>
              Unsere Partner & Sponsoren
            </h2>
            <p className="text-base md:text-lg text-slate-500 leading-relaxed">
              Wir danken unseren Partnern und Sponsoren für ihre Unterstützung – ohne sie wäre die Grömitzer Eiszeit nicht möglich.
            </p>
          </div>

          <PartnerLogos />

          {/* CTA */}
          <div className="text-center border-t border-slate-100 pt-14">
            <p className="text-slate-600 mb-5 text-base md:text-lg">
              Interesse an einer Partnerschaft?
            </p>
            <a href="mailto:jschumacher@groemitz.de?subject=Interesse%20an%20einer%20Partnerschaft">
              <Button
                size="lg"
                className="bg-slate-900 hover:bg-sky-600 text-white rounded-full px-8 py-6 transition-all duration-300 hover:shadow-lg hover:shadow-sky-200"
                data-testid="become-partner-btn"
              >
                Jetzt Partner werden
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* Logo & Info */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Snowflake className="w-8 h-8 text-sky-400" />
                <span className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                  Grömitzer Eiszeit
                </span>
              </div>
              <p className="text-slate-400 mb-4">
                Schlittschuhlaufen mit Meerblick - ein einzigartiges Wintererlebnis an der Ostsee.
              </p>
              <p className="text-slate-400 text-sm">
                27.11.2026 - 06.01.2027
              </p>
            </div>
            
            {/* Location */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Standort
              </h4>
              <div className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-sky-400 mt-1" />
                <div>
                  <p>Wiese an der Uferstraße</p>
                  <p>23743 Grömitz</p>
                  <p>Schleswig-Holstein</p>
                </div>
              </div>
            </div>
            
            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
                Kontakt
              </h4>
              <div className="space-y-3">
                <a href="tel:+4901556164249" className="flex items-center gap-3 text-slate-400 hover:text-sky-400 transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>0155 61642249</span>
                </a>
                <a href="mailto:info@groemitzereiszeit.de" className="flex items-center gap-3 text-slate-400 hover:text-sky-400 transition-colors">
                  <Mail className="w-5 h-5" />
                  <span>info@groemitzereiszeit.de</span>
                </a>
                <div className="flex items-center gap-3 pt-2">
                  <a
                    href="https://www.instagram.com/groemitzereiszeit/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Grömitzer Eiszeit auf Instagram"
                    data-testid="social-instagram"
                    className="w-10 h-10 rounded-full bg-slate-800 hover:bg-gradient-to-br hover:from-pink-500 hover:to-orange-400 text-slate-300 hover:text-white flex items-center justify-center transition-all"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.facebook.com/people/Gr%C3%B6mitzer-Eiszeit/61594520558546/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Grömitzer Eiszeit auf Facebook"
                    data-testid="social-facebook"
                    className="w-10 h-10 rounded-full bg-slate-800 hover:bg-sky-600 text-slate-300 hover:text-white flex items-center justify-center transition-all"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-slate-500 text-sm">
            <p>© 2026 Grömitzer Eiszeit. Alle Rechte vorbehalten.</p>
            <div className="flex items-center gap-6">
              <Link to="/impressum" className="hover:text-sky-400 transition-colors">
                Impressum
              </Link>
              <Link to="/agb" className="hover:text-sky-400 transition-colors" data-testid="footer-agb">
                AGB
              </Link>
              <Link to="/datenschutz" className="hover:text-sky-400 transition-colors">
                Datenschutzerklärung
              </Link>
              <button 
                onClick={() => window.openCookieSettings && window.openCookieSettings()}
                className="hover:text-sky-400 transition-colors"
                data-testid="footer-cookie-settings"
              >
                Cookie-Einstellungen
              </button>
              <Link to="/admin" className="hover:text-sky-400 transition-colors text-slate-600" data-testid="admin-link">
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
